import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import StaticContent from '../../../data/static-content/shared/static-content.json';
import {
  BUTTON_THEMES,
  BUTTON_TYPES,
  EMAIL_SUCCESS,
  FieldsetOrientation,
  ICON_TYPES,
  InputType,
  NOTIFICATION_TYPES,
} from '../../../shared/constants';
import { DOMAINS } from '../../../shared/constants/Miscellaneous.constants';
import { LeadType } from '../../../shared/constants/Tracking.constants';
import { IFormFieldset, IFormValues, IImage } from '../../../types/shared';

import PrimaryButton from '../../atoms/primary-button/PrimaryButton';
import Textarea from '../../atoms/text-area/Textarea';

import {
  DEQ_COMPS,
  DEQ_ERROR_TYPES,
  DEQ_TYPES,
  trackFormStepEvent,
} from '../../../utils/deq';
import {
  DEQ_PAGE_TYPES,
  FORM_TRACKING,
} from '../../../utils/deq/deq.constants';
import {
  FORM_TRACKING_STATUS,
  formTrackPageView,
} from '../../../utils/deq/formTrackPageView';
import { trackClick } from '../../../utils/deq/trackGenericClick';
import { trackInputErrors } from '../../../utils/deq/trackInputErrors';
import { copySelectorHelper } from '../../../utils/deq/trackInputErrors.helpers';
import { FORM_STATES } from '../../../utils/forms/constants';
import { ReactMarkdownExtended } from '../../../utils/generic';
import {
  LABELS,
  languageHelper,
  TRACKING_LABELS,
} from '../../../utils/language';
import {
  setMessageBasedOnCode,
  trackFormattedQuestion,
} from '../../../utils/string-utils';

import mq from '../../../styles/breakpoints';
import Colors from '../../../styles/colors';
import { Body, H3, Label, SmallText } from '../../../styles/generic';
import Spacings from '../../../styles/Spacings';

import { contactFormData } from '../../../data/static-content/forms/contact-form/static-content';
import {
  feedbackFormData,
  feedbackFormEmailTemplate,
} from '../../../data/static-content/forms/feedback-form/static-content';
import {
  createGraphMailTemplate,
  handleReCaptcha,
} from '../../../services/re-captcha.service';
import FormFieldset from '../../molecules/form-field-set/FormFieldset';
import FormStatusCard from '../../molecules/form-status-card/FormStatusCard';
import Form from '../form/Form';

interface Props {
  id: string;
  formTitle?: string;
  fieldset: IFormFieldset[];
  successImage?: string | IImage;
  productName: string;
  pageType: string;
  currentPage: string;
  className?: string;
}

const FeedbackForm = ({
  id,
  formTitle,
  fieldset,
  successImage,
  productName,
  pageType,
  currentPage,
  className,
}: Props) => {
  const [formState, setFormState] = useState(FORM_STATES.default);
  const [toggleClick, setToggleClick] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const questionnaireCopy = StaticContent.smallQuestionaire;
  const extraLabelCopy = questionnaireCopy.extraLabels;
  const { lang } = languageHelper();
  const deqFormName = `feedback-form ${productName}`;
  const [showTextarea, setShowTextarea] = useState(false);

  const scoreId = 'score';
  const feedbackId = 'feedback';

  const emailUrl = `${DOMAINS.production}/${currentPage}`;
  const emailScoreId = `{${scoreId}}`;
  const emailUserInputId = `{${feedbackId}}`;

  const staticEmailTemplate = feedbackFormEmailTemplate(
    emailUrl,
    emailScoreId,
    emailUserInputId
  );

  const handleSendEmailSuccess = (response: { message: string }) => {
    if (response.message === EMAIL_SUCCESS.SEND) {
      setFormState(FORM_STATES.success);
      trackFormStepEvent(
        pageType,
        id,
        FORM_TRACKING_STATUS.endSuccess,
        deqFormName,
        '',
        LeadType.LEAD_SOFT
      );
    }
    return response;
  };

  const handleSendEmailError = (response: {
    statusCode?: number;
    statusText?: string;
    url?: string;
    error?: string;
    message?: string;
    name?: string;
  }) => {
    const { statusCode, statusText, url, error, name, message } = response;
    const isTypeError = response instanceof TypeError;

    setFormState(FORM_STATES.error);
    trackFormStepEvent(
      pageType,
      id,
      FORM_TRACKING_STATUS.endFail,
      deqFormName,
      undefined,
      undefined,
      {
        type: DEQ_ERROR_TYPES.technical,
        code: isTypeError ? name : statusCode,
        source: url,
        message: isTypeError
          ? message
          : statusText ||
            error ||
            setMessageBasedOnCode(statusCode) ||
            TRACKING_LABELS.errorMessage[lang],
      }
    );
  };

  const handleSubmit = async (formResult: IFormValues) => {
    setFormState(FORM_STATES.loading);

    const emailTemplate = createGraphMailTemplate(
      formResult,
      staticEmailTemplate.emailAddresses,
      staticEmailTemplate.title,
      staticEmailTemplate.body
    );

    handleReCaptcha(
      emailTemplate,
      handleSendEmailSuccess,
      handleSendEmailError
    );
  };

  const toggleTextarea = (event: React.MouseEvent<HTMLInputElement>) => {
    trackClick(
      trackFormattedQuestion(
        fieldset[0].label || '',
        (event.target as HTMLInputElement).value
      ),
      DEQ_COMPS.feedbackForm,
      DEQ_TYPES.radio
    );
    if (!showTextarea) {
      setShowTextarea(true);

      formTrackPageView(
        DEQ_PAGE_TYPES.generic,
        id,
        deqFormName,
        (formTitle || fieldset[0].label || '')?.toLowerCase(),
        FORM_TRACKING.stepStart,
        FORM_TRACKING.version,
        LeadType.LEAD_SOFT,
        undefined,
        FORM_TRACKING.stepName
      );
    }
  };

  useEffect(() => {
    if (
      formState !== FORM_STATES.success &&
      formState !== FORM_STATES.loading
    ) {
      if (formRef) {
        const requiredCopy = copySelectorHelper(
          formRef,
          '[data-testid="notification-text"]',
          lang
        );

        trackInputErrors(DEQ_TYPES.formInput, requiredCopy);
      }
    }
  }, [toggleClick]);

  const onClickSubmitHandler = () => {
    setToggleClick(!toggleClick);
  };

  return (
    <div className={className} data-testid="feedback-form">
      <ContentContainer>
        {formState === FORM_STATES.success && (
          <>
            <ImageContainer>
              <Image src={successImage as string} />
            </ImageContainer>
            <StyledBody>{questionnaireCopy.lastSlide.title}</StyledBody>
          </>
        )}
        <FormStatusCard
          title={contactFormData.userFeedback.error.title[lang]}
          statusType={NOTIFICATION_TYPES.error}
          show={formState === FORM_STATES.error}
          customPrimaryClick={() => setFormState(FORM_STATES.default)}
          hasShadow={false}
        >
          <ReactMarkdownExtended componentType={DEQ_COMPS.feedbackForm}>
            {contactFormData.userFeedback.error.description[lang]}
          </ReactMarkdownExtended>
        </FormStatusCard>
        <FormStatusCard
          statusType={NOTIFICATION_TYPES.loading}
          show={formState === FORM_STATES.loading}
          hasShadow={false}
        >
          <ReactMarkdownExtended componentType={DEQ_COMPS.feedbackForm}>
            {contactFormData.userFeedback.loading.email[lang]}
          </ReactMarkdownExtended>
        </FormStatusCard>
        {formState === FORM_STATES.default && (
          <Form
            id={id}
            deqFormName={deqFormName}
            stepName={formTitle || fieldset[0].label}
            submit={handleSubmit}
            deqFormType={LeadType.LEAD_SOFT}
            trackPageView={false}
            ref={formRef}
          >
            {fieldset.map((item, index) => (
              <div key={`${index}-${item.label}`}>
                <StyledH3>{item.label}</StyledH3>
                <FormFieldWrapper showFields={!showTextarea}>
                  <WidthWrapper wrapperWidth="236">
                    <LabelWrapper>
                      <Label>{extraLabelCopy.negative}</Label>
                      <Label>{extraLabelCopy.positive}</Label>
                    </LabelWrapper>
                    <InputWrapper inputLayout={item.layout}>
                      <FormFieldset
                        key={index}
                        isRequired={false}
                        name={scoreId}
                        fields={item.radiobuttons}
                        layout={FieldsetOrientation.INLINE}
                        inputStyle={InputType.ENRICHED_RADIO}
                        customRadioHandler={toggleTextarea}
                        hideRadioInput
                      />
                    </InputWrapper>
                  </WidthWrapper>
                </FormFieldWrapper>
              </div>
            ))}
            {showTextarea && (
              <>
                <StyledTextarea
                  id={feedbackId}
                  name={feedbackId}
                  label={feedbackFormData.userInput.label[lang]}
                  placeholder={feedbackFormData.userInput.placeholder[lang]}
                  required
                />
                <StyledPrimaryButton
                  type={BUTTON_TYPES.submit}
                  data-testid="submit-button"
                  buttonAction={BUTTON_TYPES.submit}
                  label={LABELS.submit[lang]}
                  icon={ICON_TYPES.none}
                  buttonTheme={BUTTON_THEMES.highlight}
                  deqComponentTitle={formTitle}
                  deqCustomAttributes={{
                    componentType: DEQ_COMPS.feedbackForm,
                    elementAction: BUTTON_TYPES.submit,
                  }}
                  customOnClick={() =>
                    setTimeout(() => {
                      onClickSubmitHandler();
                    }, 0)
                  }
                />
                <DisclaimerWrapper>
                  <DisclaimerCopy>
                    <ReactMarkdownExtended
                      componentType={DEQ_COMPS.feedbackForm}
                    >
                      {feedbackFormData.disclaimerCopy.body[lang]}
                    </ReactMarkdownExtended>
                  </DisclaimerCopy>
                </DisclaimerWrapper>
              </>
            )}
          </Form>
        )}
      </ContentContainer>
    </div>
  );
};

export default FeedbackForm;

const ContentContainer = styled.div`
  width: 100%;

  ${mq.from.breakpoint.M`
    max-width: 732px;
    margin: 0 auto;
  `}
`;

const StyledH3 = styled(H3)`
  margin-bottom: ${Spacings.M};
  margin-top: 0;
  padding-top: 0;
  text-align: center;
`;

const StyledSmallText = styled(SmallText)`
  color: ${Colors.abnMutedGreen400};
  font-weight: 500;
`;

const ImageContainer = styled.div`
  height: 122px;
  margin: 0 auto;
  padding-bottom: ${Spacings.S};
  position: relative;
  width: 122px;

  ${mq.from.breakpoint.M`
    width: 84px;
    height: 84px;
  `}

  ${mq.from.breakpoint.L`
    width: 92px;
    height: 92px;
  `}
`;

const Image = styled.img`
  height: 100%;
  object-fit: cover;
  transition: all 0.1s ease-in-out;
  width: 100%;
`;

const StyledBody = styled(Body)`
  text-align: center;
`;

const WidthWrapper = styled.div<{ wrapperWidth: string }>`
  margin: 0 auto;
  max-width: 343px;

  ${mq.from.breakpoint.M`
    max-width: ${({ wrapperWidth }: { wrapperWidth: string }) =>
      wrapperWidth}px;
  `}
`;

const LabelWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: ${Spacings.S};

  & > div,
  span,
  p {
    color: ${Colors.abnMutedGreen400};
  }
`;

const InputWrapper = styled.div<{
  inputLayout?: string;
}>`
  display: flex;
  flex-direction: ${({ inputLayout }: { inputLayout?: string }) =>
    inputLayout === 'inline' ? 'row' : 'column'};
  gap: ${Spacings.XS};
  width: 100%;
`;

const StyledPrimaryButton = styled(PrimaryButton)`
  width: 100%;

  ${mq.from.breakpoint.M`
    width: auto;
  `}
`;

const StyledTextarea = styled(Textarea)`
  margin-bottom: ${Spacings.S};
  margin-top: ${Spacings.M};
`;

const FormFieldWrapper = styled.div<{ showFields: boolean }>`
  display: ${({ showFields }) => (showFields ? 'block' : 'none')};
`;

const DisclaimerCopy = styled(Body)`
  color: ${Colors.abnNeutral500};
  font-size: 14px;
  line-height: 20px;
`;

const DisclaimerWrapper = styled.div`
  margin-top: ${Spacings.S};
`;
